import { Random } from 'Utility/Random';
import Game from 'Game';
import { getAudioSpriteKey } from './AudioHelper';
import { Howl } from 'howler';

export interface IAudioParams {
    loop: boolean;
    allowMultipleInstances: boolean;
    volume: number;
    seek: number;
}

export enum AudioTrackType {
    SFX = 'sfx',
    MUSIC = 'music',
}

const defaultParams: IAudioParams = {
    loop: false,
    allowMultipleInstances: true,
    volume: 1,
    seek: 0,
};

export default class AudioClip {
    public readonly id: string;
    public readonly name: string;
    public track: AudioTrackType;

    public howl: Howl;
    private soundID: number;

    private readonly sprite: string;
    private _loop: boolean = false;
    private _volume: number = 1;

    private _onCompleteCB: (clip: AudioClip) => void;
    private _allowMultipleInstances: boolean;

    constructor(
        clipName: string,
        track: AudioTrackType,
        params?: Partial<IAudioParams>
    ) {
        this.id = Random.ID();
        this.name = clipName;
        this.track = track;

        const audioSpriteKey = getAudioSpriteKey(clipName);

        // If clip is in contained in a audio sprite, then get resource via audioSpriteKey and set sprite name
        if (audioSpriteKey) {
            this.howl = this.getHowlResource(audioSpriteKey);
            this.sprite = clipName;
        }
        // Otherwise get resource via clipName
        else {
            this.howl = this.getHowlResource(clipName);
        }

        if (params) {
            params = {
                ...defaultParams,
                ...params,
            };
        } else {
            params = defaultParams;
        }

        this.loop = params.loop;
        this.volume = params.volume;
        this.allowMultipleInstances = params.allowMultipleInstances;
    }

    public onComplete(cb: (clip: AudioClip) => void) {
        this._onCompleteCB = cb;
    }

    public play() {
        if (!this.howl) {
            return;
        }

        if (this.track === AudioTrackType.SFX) {
            this.soundID = this.howl.play(this.sprite);
            this.howl.volume(this.volume, this.soundID);
            this.howl.loop(this.loop, this.soundID);
            if (!this.loop)
                this.howl.once(
                    'end',
                    () => {
                        this.play();
                        if (this._onCompleteCB) this._onCompleteCB(this);
                    },
                    this.soundID
                );
        }


        if (
            this.track === AudioTrackType.MUSIC &&
            (!this.soundID || !this.howl.playing(this.soundID))
        ) {
            this.soundID = this.howl.play();
            this.howl.volume(this.volume, this.soundID);
            this.howl.loop(this.loop, this.soundID);
            this.howl.once(
                'playerror',
                (e, m) => {
                    console.warn(e, m, 'onplayerror');
                    this.howl.once('unlock', () => {
                        this.play();
                    });
                },
                this.soundID
            );
        }
    }

    public destroy() {
        if (!this.howl) {
            return;
        }

        this.howl.stop(this.soundID);
        this.soundID = null;
        this.howl = null;
    }

    get volume(): number {
        return this._volume;
    }

    set volume(value: number) {
        this._volume = value;
        if (this.howl && this.soundID) {
            this.howl.volume(value, this.soundID);
        }
    }

    get loop(): boolean {
        return this._loop;
    }

    set loop(value: boolean) {
        this._loop = value;

        if (this.howl && this.soundID) {
            this.howl.loop(value, this.soundID);
        }
    }

    get allowMultipleInstances(): boolean {
        return this._allowMultipleInstances;
    }

    set allowMultipleInstances(value: boolean) {
        this._allowMultipleInstances = value;
    }

    private getHowlResource(id: string): Howl {
        if (Game.resource.existsAndLoaded(id)) {
            const resource: any = Game.resource.get(id);
            if (resource instanceof Howl) {
                return Game.resource.get(id);
            }
        }
        return null;
    }
}
